import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { Box, Camera, CircleDot, Disc3, Link2, Maximize2, Minimize2, ScanLine, Triangle } from 'lucide-react';
import type { SteelCoilCameraPreset } from './SteelCoilScene3D';

const SteelCoilScene3D = lazy(() => import('./SteelCoilScene3D'));

type Props = {
  axis: 'longitudinal' | 'transverse';
  outerDiameterM: number;
  innerDiameterM: number;
  coilWidthM: number;
  beamSpreadM: number;
  beddingLengthM: number;
  chockHeightM: number;
  cogHeightM: number;
  halfLoopPairCount: number;
  chockPasses?: boolean;
  assessmentPasses?: boolean;
  directLashingCount: number;
  directLashingAttachmentHeightM: number;
  directLashingVerticalAngleDeg?: number;
  directLashingHorizontalAngleDeg: number;
};

const presets: Array<[SteelCoilCameraPreset, string, typeof Camera]> = [
  ['perspective', '3D', Camera], ['front', 'Ön', CircleDot], ['side', 'Yan', Box], ['top', 'Üst', ScanLine],
];

export default function SteelCoil3DCard(props: Props) {
  const [preset, setPreset] = useState<SteelCoilCameraPreset>('perspective');
  const [visible, setVisible] = useState({ coil: true, lashings: true, supports: true });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const updateFullscreen = () => setIsFullscreen(document.fullscreenElement === sectionRef.current);
    document.addEventListener('fullscreenchange', updateFullscreen);
    return () => document.removeEventListener('fullscreenchange', updateFullscreen);
  }, []);
  const toggleFullscreen = async () => {
    if (document.fullscreenElement) await document.exitFullscreen();
    else await sectionRef.current?.requestFullscreen();
  };
  const visibilityControls = [
    ['coil', 'Coil', Disc3], ['lashings', 'Halatlar', Link2], ['supports', 'Takoz ve bedding', Triangle],
  ] as const;

  return <section ref={sectionRef} className="overflow-hidden border-y border-slate-200 bg-white dark:border-slate-700 dark:bg-[#182235]">
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-3 dark:border-slate-700">
      <div className="flex items-center gap-2"><Box className="h-4 w-4 text-orange-500" /><h2 className="text-sm font-bold tracking-normal !text-slate-900 dark:!text-white">3D yerleşim</h2></div>
      <div className="flex items-center gap-2">
        <div className="flex rounded-md border border-slate-300 p-0.5 dark:border-slate-600">
          {visibilityControls.map(([key, label, Icon]) => <button key={key} type="button" aria-pressed={visible[key]} onClick={() => setVisible(current => ({ ...current, [key]: !current[key] }))} title={`${label} görünürlüğü`} className={`grid h-8 w-8 place-items-center rounded ${visible[key] ? 'text-slate-700 dark:text-slate-200' : 'bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500'}`}><Icon className="h-3.5 w-3.5" /></button>)}
        </div>
        <div className="flex rounded-md border border-slate-300 p-0.5 dark:border-slate-600">
          {presets.map(([value, label, Icon]) => <button key={value} type="button" onClick={() => setPreset(value)} title={`${label} kamera`} className={`flex h-8 items-center gap-1 rounded px-2.5 text-xs font-semibold ${preset === value ? 'bg-orange-600 text-white' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700'}`}><Icon className="h-3.5 w-3.5" /><span className="hidden sm:inline">{label}</span></button>)}
        </div>
        <button type="button" onClick={toggleFullscreen} title={isFullscreen ? 'Tam ekrandan çık' : 'Tam ekran'} className="grid h-9 w-9 place-items-center rounded-md border border-slate-300 text-slate-600 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700">
          {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
        </button>
      </div>
    </div>
    <div className={`relative w-full bg-slate-50 ${isFullscreen ? 'h-[calc(100vh-65px)]' : 'h-[340px] sm:h-[420px]'}`}>
      <Suspense fallback={<div className="grid h-full place-items-center text-xs text-slate-500">3D sahne hazırlanıyor...</div>}>
        <SteelCoilScene3D {...props} cameraPreset={preset} showCoil={visible.coil} showLashings={visible.lashings} showSupports={visible.supports} />
      </Suspense>
    </div>
  </section>;
}
